import { useMemo, useState, MouseEvent, useCallback } from 'react';
import { PlusIcon } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import Toggle from '@components/toggle/toggle';
import CustomInput from '@components/custom-input/custom-input.component';
import { Select } from '@components/custom-select/select';
import {
    SelectItem,
    SelectItemType,
} from '@components/custom-select/select-item';
import DatePicker from '@components/date-picker/date-picker.component';
import BasicButton from '@components/basic-button/basic-button.component';
import { Gateway } from '@app-types/payment.type';
import { validateSchema } from '@utils/validate-schema';

import { NewPaymentRow } from '../../components/new-payment-row/new-payment-row';

import styles from './add-payments.module.scss';
import newPaymentSchema from './validation-schema';

export type NewPayment = {
    uuid: string;
    amount: number;
    gatewayId: number;
    exchangeRate: number;
    currency: string;
    date: Date;
};

type Props = {
    gateways: Gateway[];
    payments: NewPayment[];
    rates: Record<string, number>;
    onAddPayment: (payment: NewPayment) => void;
    onRemovePayment: (uuid: string) => void;
};

const AddPayments = ({
    gateways,
    payments,
    rates,
    onAddPayment,
    onRemovePayment,
}: Props) => {
    const [amount, setAmount] = useState<number>(0);
    const [gatewayId, setGatewayId] = useState<number>();
    const [date, setDate] = useState<Date>();
    const [autoExchangeRate, setAutoExchangeRate] = useState(false);
    const [rate, setRate] = useState<number>();
    const [customRate, setCustomRate] = useState<number>();
    const [currency, setCurrency] = useState<string>('USD');

    const gatewayItems: SelectItemType[] = useMemo(() => {
        return gateways.map((g) => ({
            id: g.id,
            label: g.name,
            supportingText: g.currency,
        }));
    }, [gateways]);

    const selectGateway = useCallback(
        (item: SelectItemType) => {
            const gateway = gateways.find((g) => g.id === Number(item.id));
            if (!gateway) return;
            setGatewayId(gateway.id);
            setRate(rates[gateway.currency]);
            setCurrency(gateway.currency);
        },
        [gateways, rates]
    );

    const submitPayment = () => {
        const payload: any = {
            uuid: uuidv4(),
            amount,
            gatewayId,
            date,
            currency,
        };
        if (customRate && customRate > 0) {
            payload.exchangeRate = customRate;
        } else {
            payload.exchangeRate = rate;
        }
        const errors = validateSchema(payload, newPaymentSchema);
        if (errors) return console.error(errors);
        onAddPayment(payload as NewPayment);
        setAmount(0);
        setGatewayId(undefined);
        setCustomRate(undefined);
        setCurrency('USD');
    };

    const handlePaymentDeletion = (ev: MouseEvent) => {
        ev.preventDefault();
        const target = (ev.target as HTMLElement).closest(
            "[data-action='delete-payment']"
        ) as HTMLElement;
        if (!target) return;
        const id = target.parentElement?.dataset.id as string;
        return onRemovePayment(id);
    };

    return (
        <>
            {payments.length > 0 && (
                <ul className={styles.payments} onClick={handlePaymentDeletion}>
                    <div className={styles.listHeader}>
                        <label className={styles.amount}>Monto</label>
                        <label className={styles.gateway}>Método</label>
                        <label className={styles.date}>Fecha</label>
                        <label className={styles.actions} />
                    </div>
                    {payments.map((p) => {
                        const gateway = gateways.find(
                            (g) => g.id === p.gatewayId
                        );
                        return (
                            <NewPaymentRow
                                key={p.uuid}
                                payment={p}
                                gatewayLabel={gateway!.name}
                            />
                        );
                    })}
                </ul>
            )}
            <div className={styles.newPaymentRecord}>
                <div className={styles.leftSide}>
                    <CustomInput
                        width="80px"
                        hideClear={true}
                        placeholder="Monto"
                        type="number"
                        value={amount.toString()}
                        onChange={(val) => setAmount(Number(val))}
                    />
                    <div style={{ width: 180 }}>
                        <Select
                            value={gatewayId?.toString()}
                            items={gatewayItems}
                            placeholder="Método de pago"
                            onSelect={selectGateway}
                        >
                            {gatewayItems.map((gateway) => (
                                <SelectItem
                                    key={gateway.id}
                                    id={gateway.id}
                                    supportingText={gateway.supportingText}
                                >
                                    {gateway.label}
                                </SelectItem>
                            ))}
                        </Select>
                    </div>
                    {!autoExchangeRate && currency !== 'USD' && (
                        <div style={{ width: 100 }}>
                            <CustomInput
                                placeholder="Tasa"
                                value={customRate?.toString()}
                                type="number"
                                hideClear
                                onChange={(r) => setCustomRate(Number(r))}
                            />
                        </div>
                    )}
                </div>
                <DatePicker onDate={setDate} />
            </div>
            {currency !== 'USD' && (
                <div className={styles.exchangeRate}>
                    <Toggle
                        value={autoExchangeRate}
                        onToggle={setAutoExchangeRate}
                    />
                    <label>Tomar tasa del día: {rate}</label>
                </div>
            )}
            <div className={styles.alignRight} style={{ marginTop: 20 }}>
                <BasicButton onClick={submitPayment}>
                    <PlusIcon size={16} strokeWidth={2.5} />
                    <span>Agregar pago</span>
                </BasicButton>
            </div>
        </>
    );
};

export { AddPayments };
